import { cldOpt } from "./img";

// Shared site-markdown renderer used by CategoryPage + ProductPage.
// Supports: #-###### headings, - / • bullets, 1. ordered lists, **bold**,
// *italic*, ***bold italic***, <u>underline</u>, [label](url) links,
// ![alt](url) images and GFM pipe tables.

const TABLE_ROW_RE = /^\|.*\|$/;
const TABLE_SEP_RE = /^\|(?: *:?-{2,}:? *\|)+$/;
const splitTableRow = (line) =>
  line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split(/(?<!\\)\|/)
    .map((c) => c.trim().replace(/\\\|/g, "|"));

// Plain-text version (for meta descriptions etc.)
export const stripMdText = (t) =>
  (t || "")
    .replace(/^\s*\|(?: *:?-{2,}:? *\|)+\s*$/gm, "") // table separator rows
    .replace(/\\\|/g, "\u0000")
    .replace(/\|/g, " ") // table pipes
    .replace(/\u0000/g, "|")
    .replace(/^#{1,6}\s*/gm, "")
    .replace(/^\s*[-*•]\s+/gm, "")
    .replace(/^\s*\d+\.\s+/gm, "")
    .replace(/<\/?u>/gi, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/!\[[^\]]*\]\([^)]+\)/g, "")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/\s+/g, " ")
    .trim();

export function renderMd(text = "") {
  if (!text) return null;
  // Normalize: force every heading, list, image and table row line to sit in
  // its own block by ensuring a blank line above/below. Fixes the case where
  // the WYSIWYG serializer emits `## Title\nBody line` (single newline) which
  // the block splitter previously treated as one paragraph.
  text = text
    .replace(/\r\n?/g, "\n")
    .replace(/([^\n])\n(#{1,6}\s)/g, "$1\n\n$2") // heading needs blank line above
    .replace(/(#{1,6}[^\n]*)\n(?!\n)/g, "$1\n\n"); // ...and blank line below
  // Split a string into React nodes handling **bold**, *italic*, <u>underline</u>
  // AND [label](url) — including links nested inside bold like **[label](url)**.
  // Links are extracted first so URLs containing `*` are safe.
  const renderRich = (str) => {
    // Strip surrounding **...** when they enclose a link — the anchor already
    // renders with font-semibold, so extra `**` markers would leak as literal text.
    let cleaned = str.replace(/\*\*(\[[^\]]+\]\([^)]+\))\*\*/g, "$1");
    // Also strip **...** immediately inside <u>...</u> for the same reason: the
    // link/anchor inside is already visually strong, and rendering the literal
    // asterisks confused users when the WYSIWYG toolbar wrapped a link in <u>.
    cleaned = cleaned.replace(/<u>\s*\*\*(.+?)\*\*\s*<\/u>/gi, "<u>$1</u>");
    // Tokenize links and <u>…</u> first so the emphasis regex never eats their contents.
    // Markdown title attr is used as a follow directive: `[text](href "nofollow")`
    // adds rel=nofollow, `[text](href "dofollow")` forces removal. Absence = dofollow.
    const tokens = [];
    const linkRe = /\[([^\]]+)\]\(([^)\s]+)(?:\s+"([^"]*)")?\)/g;
    const underlineRe = /<u>([\s\S]+?)<\/u>/gi;
    const withTokens = cleaned
      .replace(linkRe, (_, label, url, title) => {
        const i = tokens.length;
        tokens.push({ kind: "link", label, url, title: title || "" });
        return `\u0000T${i}\u0000`;
      })
      .replace(underlineRe, (_, inner) => {
        const i = tokens.length;
        tokens.push({ kind: "u", inner });
        return `\u0000T${i}\u0000`;
      });

    const nodes = [];
    // Then split by ***bold+italic***, **bold**, *italic*, and token placeholders.
    const partsRe = /(\u0000T\d+\u0000|\*\*\*[^*]+\*\*\*|\*\*[^*]+\*\*|\*[^*]+\*)/g;
    let last = 0;
    let m;
    let key = 0;
    const pushToken = (chunk) => {
      const tok = tokens[Number(chunk.match(/\u0000T(\d+)\u0000/)[1])];
      if (tok.kind === "link") {
        // Same-host absolute URLs count as INTERNAL — don't force _blank and
        // don't inject rel=nofollow just because they start with https://.
        let external = /^https?:\/\//i.test(tok.url);
        if (external && typeof window !== "undefined") {
          try {
            const u = new URL(tok.url, window.location.origin);
            if (u.hostname === window.location.hostname) external = false;
          } catch { /* malformed URL — keep external=true */ }
        }
        // Honor the Markdown title as a follow directive:
        //   "nofollow" → rel=nofollow (any link)
        //   "dofollow" → force NO nofollow (even for external)
        //   absent     → dofollow for internal, dofollow for external (SEO-first)
        const title = (tok.title || "").toLowerCase();
        const isNofollow = title.includes("nofollow");
        const relParts = [];
        if (external) relParts.push("noopener", "noreferrer");
        if (isNofollow) relParts.push("nofollow");
        nodes.push(
          <a
            key={`a${key++}`}
            href={tok.url}
            target={external ? "_blank" : undefined}
            rel={relParts.length ? relParts.join(" ") : undefined}
            className="font-semibold text-black underline underline-offset-2 hover:no-underline"
          >
            {renderRich(tok.label)}
          </a>
        );
      } else if (tok.kind === "u") {
        // Recurse so **bold**, *italic*, [links](…) inside <u>…</u> still render.
        nodes.push(
          <u key={`u${key++}`} className="underline underline-offset-2">
            {renderRich(tok.inner)}
          </u>
        );
      }
    };
    while ((m = partsRe.exec(withTokens)) !== null) {
      if (m.index > last) nodes.push(<span key={`s${key++}`}>{withTokens.slice(last, m.index)}</span>);
      const seg = m[0];
      if (/^\u0000T\d+\u0000$/.test(seg)) {
        pushToken(seg);
      } else if (seg.startsWith("***")) {
        nodes.push(
          <strong key={`bi${key++}`} className="text-black">
            <em>{seg.slice(3, -3)}</em>
          </strong>
        );
      } else if (seg.startsWith("**")) {
        nodes.push(
          <strong key={`b${key++}`} className="text-black">
            {seg.slice(2, -2)}
          </strong>
        );
      } else if (seg.startsWith("*")) {
        nodes.push(<em key={`i${key++}`}>{seg.slice(1, -1)}</em>);
      }
      last = m.index + seg.length;
    }
    if (last < withTokens.length) nodes.push(<span key={`s${key++}`}>{withTokens.slice(last)}</span>);
    return nodes;
  };
  return text.split(/\n\n+/).map((block, i) => {
    const trimmed = block.trim();
    // Pipe table block
    const blockLines = trimmed.split("\n").map((l) => l.trim());
    if (blockLines.length >= 2 && TABLE_ROW_RE.test(blockLines[0]) && TABLE_SEP_RE.test(blockLines[1])) {
      const header = splitTableRow(blockLines[0]);
      const rows = blockLines
        .slice(2)
        .filter((l) => TABLE_ROW_RE.test(l) && !TABLE_SEP_RE.test(l))
        .map(splitTableRow);
      return (
        <div key={i} className="mt-6 overflow-x-auto" data-testid="md-table">
          <table className="w-full border-collapse border-2 border-black text-sm">
            <thead>
              <tr>
                {header.map((h, j) => (
                  <th
                    key={j}
                    className="border border-black bg-black px-4 py-2.5 text-left text-xs font-bold uppercase tracking-wider text-white [&_a]:text-white [&_em]:text-white [&_strong]:text-white [&_u]:text-white"
                  >
                    {renderRich(h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri} className={ri % 2 ? "bg-zinc-50" : "bg-white"}>
                  {header.map((_, ci) => (
                    <td key={ci} className="border border-black px-4 py-2.5 align-top leading-relaxed text-zinc-700">
                      {renderRich(r[ci] ?? "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    // Standalone image line
    const img = trimmed.match(/^!\[([^\]]*)\]\(([^)\s]+)\)$/);
    if (img) {
      return (
        <figure key={i} className="my-6 overflow-hidden border-2 border-black">
          <img src={cldOpt(img[2], 1200)} alt={img[1]} loading="lazy" className="h-auto w-full object-cover" />
        </figure>
      );
    }
    const hMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (hMatch) {
      const level = hMatch[1].length;
      const inner = hMatch[2];
      const HTag = `h${level}`;
      const cls = {
        1: "mt-10 font-display text-2xl font-black uppercase tracking-tight text-black sm:text-3xl",
        2: "mt-8 font-display text-xl font-black uppercase tracking-tight text-black",
        3: "mt-6 font-display text-lg font-bold uppercase tracking-tight text-black",
        4: "mt-6 font-display text-base font-bold uppercase tracking-widest text-black",
        5: "mt-6 text-sm font-bold uppercase tracking-widest text-black",
        6: "mt-4 text-xs font-bold uppercase tracking-[0.2em] text-zinc-600",
      }[level];
      return <HTag key={i} className={cls}>{renderRich(inner)}</HTag>;
    }
    if (trimmed.startsWith("- ") || trimmed.startsWith("• ")) {
      const items = trimmed.split("\n").filter((l) => l.trim().startsWith("- ") || l.trim().startsWith("• "));
      return (
        <ul key={i} className="mt-3 list-disc space-y-1 pl-5 text-sm leading-relaxed text-zinc-700">
          {items.map((li, j) => <li key={j}>{renderRich(li.trim().slice(2))}</li>)}
        </ul>
      );
    }
    if (/^\d+\.\s+/.test(trimmed)) {
      const items = trimmed.split("\n").filter((l) => /^\d+\.\s+/.test(l.trim()));
      return (
        <ol key={i} className="mt-3 list-decimal space-y-1 pl-5 text-sm leading-relaxed text-zinc-700">
          {items.map((li, j) => <li key={j}>{renderRich(li.trim().replace(/^\d+\.\s+/, ""))}</li>)}
        </ol>
      );
    }
    return <p key={i} className="mt-3 text-sm leading-relaxed text-zinc-700 sm:text-base">{renderRich(trimmed)}</p>;
  });
}
